/**
 * Common API Contract Schemas
 * Shared schemas used across all API contracts
 */

import { z } from 'zod';

// ============================================================================
// Boolean coercion — query-param safe
// ============================================================================

/**
 * Parses query-param booleans correctly: only the string "true" (or a native
 * boolean true) produces true. Everything else — "false", "0", "1", "", null,
 * undefined — produces false. Use instead of z.coerce.boolean(), which treats
 * "false" as true via JS Boolean() coercion.
 */
export const BooleanFromString = z
  .union([z.boolean(), z.string(), z.null(), z.undefined()])
  .transform((val) => {
    if (typeof val === 'boolean') return val;
    return val === 'true';
  });

export type BooleanFromStringInput = z.input<typeof BooleanFromString>;

// ============================================================================
// Pagination
// ============================================================================

export const PaginationQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(20),
  sortBy: z.string().optional(),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
});

export type PaginationQuery = z.infer<typeof PaginationQuerySchema>;

/** Shared pagination metadata schema used by both PaginatedResponse and ApiResponse */
export const PaginationMetaSchema = z.object({
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
  total: z.number().int().nonnegative(),
  totalPages: z.number().int().nonnegative(),
  hasMore: z.boolean().optional(),
});

export type PaginationMeta = z.infer<typeof PaginationMetaSchema>;

export const PaginatedResponseSchema = <T extends z.ZodType>(itemSchema: T) =>
  z.object({
    items: z.array(itemSchema),
    meta: PaginationMetaSchema,
  });

// ============================================================================
// API Response Wrappers
// ============================================================================

export const ApiErrorSchema = z.object({
  code: z.string(),
  message: z.string(),
  details: z.record(z.unknown()).optional(),
  timestamp: z.string().datetime().optional(),
  path: z.string().optional(),
  requestId: z.string().optional(),
});

export type ApiError = z.infer<typeof ApiErrorSchema>;

export const ApiResponseSchema = <T extends z.ZodType>(dataSchema: T) =>
  z.object({
    success: z.boolean(),
    data: dataSchema.optional(),
    error: ApiErrorSchema.optional(),
    meta: PaginationMetaSchema.partial().optional(),
  });

// ============================================================================
// Success Response Wrappers (for service/controller return types)
// ============================================================================

/** { success: true, data: T } */
export type SuccessResponse<T> = { success: true; data: T };

/** { success: true, data: T[], meta: PaginationMeta } */
export type SuccessPaginatedResponse<T> = { success: true; data: T[]; meta: PaginationMeta };

// ============================================================================
// Date Filters
// ============================================================================

export const DateRangeQuerySchema = z.object({
  startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});

export type DateRangeQuery = z.infer<typeof DateRangeQuerySchema>;

export const VisitDateQuerySchema = z.object({
  visitDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});

export type VisitDateQuery = z.infer<typeof VisitDateQuerySchema>;

// ============================================================================
// Common ID Params
// ============================================================================

export const UuidParamSchema = z.object({
  id: z.string().uuid(),
});

export type UuidParam = z.infer<typeof UuidParamSchema>;

export const OrderNumberParamSchema = z.object({
  orderNumber: z.string().max(20),
});

export type OrderNumberParam = z.infer<typeof OrderNumberParamSchema>;

export const TicketNumberParamSchema = z.object({
  ticketNumber: z.string().max(30),
});

export type TicketNumberParam = z.infer<typeof TicketNumberParamSchema>;

// ============================================================================
// Sort Options
// ============================================================================

export const SortOptionsSchema = z.object({
  sortBy: z.string(),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
});

export type SortOptions = z.infer<typeof SortOptionsSchema>;

// ============================================================================
// Filter Base
// ============================================================================

export const StatusFilterSchema = z.object({
  status: z.string().optional(),
});

export type StatusFilter = z.infer<typeof StatusFilterSchema>;
